/**
 * voice-v8 — Connectors-Status-API (Phase L).
 */
import type { UpdateStandAntwort } from "../../shared/update-starten.js";

interface ConnectorStatus {
  configured: boolean;
  connected: boolean;
  scope: string | null;
  connectedAt: string | null;
}

export interface ConnectorsState {
  google: ConnectorStatus;
}

export async function fetchConnectors(): Promise<ConnectorsState> {
  const res = await fetch("/api/oauth/status", { credentials: "same-origin" });
  if (!res.ok) throw new Error(`oauth status: ${res.status}`);
  return (await res.json()) as ConnectorsState;
}

export async function disconnectGoogle(): Promise<void> {
  await fetch("/api/oauth/google/disconnect", {
    method: "POST",
    credentials: "same-origin",
  });
}

// --- Browser-Extension -------------------------------------------------

export interface ExtensionStatus {
  configured: boolean;
  paired: boolean;
  connected: boolean;
  pairedAt: string | null;
  lastSeenAt: string | null;
  /** Ticket 2816: von der verbundenen Erweiterung gemeldet (ab 2.0.6), sonst null. */
  version?: string | null;
  /** Ticket 2816: die ausgelieferte Version (Bau, app-versionen.json). */
  aktuell?: string | null;
  /**
   * 07.10.2026 (Plan „mehrere Geräte“, S6): alle Browser des Kontos — verbundene zuerst (nach „verbunden seit“), dann
   * gekoppelte, gerade nicht verbundene. Fehlt bei einem älteren Server.
   */
  geraete?: BrowserGeraet[];
  /** 07.10.2026 (S6): in welchem Browser Vespera arbeitet und warum (server/extension-hub.ts geraeteStand). */
  wahl?: MacWahl;
}

/** Ein Browser, wie GET /api/ext/status und POST /api/ext/geraet ihn zeigen (server/extension-hub.ts BrowserAnzeige). */
export interface BrowserGeraet {
  /** „chrome-<Nummer der Kopplung>“ — nie der Schlüssel. */
  id: string;
  name: string;
  aktiv: boolean;
  wahl: "hand" | "auto" | null;
  verbunden: boolean;
  verbunden_seit: string | null;
  zuletzt: string | null;
  version: string | null;
  /** „macOS“, „Windows“ … — erst ab Erweiterung 2.2.0. */
  os: string | null;
  /** „gerade“, „vor 4 min“, „gesperrt“ — nur aus echten Meldungen (2.2.0), sonst null. */
  benutzt: string | null;
  /** Verbunden, meldet aber weder Betriebssystem noch Leerlauf (vor 2.2.0). */
  aeltere_erweiterung: boolean;
}

interface PairingCode {
  code: string;
  expiresAt: string | null;
}

export async function fetchExtensionStatus(): Promise<ExtensionStatus> {
  const res = await fetch("/api/ext/status", { credentials: "same-origin" });
  if (!res.ok) throw new Error(`ext status: ${res.status}`);
  return (await res.json()) as ExtensionStatus;
}

export async function generatePairingCode(): Promise<PairingCode> {
  const res = await fetch("/api/ext/pair-code", {
    method: "POST",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`pair-code: ${res.status}`);
  return (await res.json()) as PairingCode;
}

// --- PawBot Desktop / Mac-Daemon (Pre.237) ------------------------------

export interface DaemonStatus {
  connected: boolean;
  paused: boolean;
  active: boolean;
  /** Pre.236: macOS Bedienungshilfen-Permission (true/false/null=unbekannt). */
  accessibility?: boolean | null;
  reason?: string;
  /** Ticket 2816: gemeldete Version der Mac-App (die alte PawBot-App meldet keine). */
  version?: string | null;
  /** Ticket 2816: aktuelle Version der Mac-App „Vespera Desktop“. */
  aktuell?: string | null;
  /**
   * Ticket 2816: nur Konto 1 — Adresse des Downloads der neuen App: die
   * freigegebene Fassung (/api/daemon/desktop-download, Ticket 2817) oder die
   * geschützte Testversion (/downloads/1/Vespera-Desktop-<Version>.dmg),
   * sonst null.
   */
  download?: string | null;
  /** Ticket 2817: die Version der Datei hinter `download` („1.0.4“). */
  downloadVersion?: string | null;
  /** Ticket 2823: seit wann die App verbunden ist (ISO) — nur solange sie verbunden ist. */
  seit?: string | null;
  /**
   * 07.10.2026: Ein zweites Vespera Desktop desselben Kontos versucht sich zu verbinden und wird abgewiesen,
   * solange das verbundene lebt (server/daemon-bridge.ts) — wie oft und wann zuletzt; null, wenn nicht gerade.
   */
  zweiteVersuche?: { anzahl: number; zuletztIso: string } | null;
  /**
   * 07.10.2026 (Plan „mehrere Geräte“, S1): alle Macs des Kontos — verbundene zuerst (nach „verbunden seit“), dann
   * bekannte, nicht verbundene (höchstens 20). Fehlt bei einem älteren Server: dann zeigt die Karte den alten Status.
   */
  geraete?: MacGeraet[];
  /** 07.10.2026: welcher Mac aktiv ist und warum (server/daemon-bridge.ts geraeteStand). */
  wahl?: MacWahl;
  /**
   * 12.10.2026: der Stand von „Jetzt aktualisieren“ (shared/update-starten.ts) — nur Wörter, `erlaubt` nur beim Betreiberkonto.
   * Fehlt bei einem älteren Server: dann gibt es den Knopf nicht.
   */
  updateStarten?: UpdateStandAntwort;
}

/** Ein Mac, wie /api/daemon-status und POST /api/daemon/geraet ihn zeigen (server/daemon-bridge.ts GeraetAnzeige). */
export interface MacGeraet {
  id: string;
  name: string;
  /** Vespera arbeitet hier. */
  aktiv: boolean;
  /** Warum aktiv: von Hand gewählt oder automatisch — null, wenn nicht aktiv. */
  wahl: "hand" | "auto" | null;
  verbunden: boolean;
  verbunden_seit: string | null;
  /** Zuletzt gesehen — nur bei bekannten, gerade nicht verbundenen Macs. */
  zuletzt: string | null;
  /** In der Karte getrennt — erst Neu-Koppeln lässt ihn wieder herein. */
  getrennt: boolean;
  version: string | null;
  bedienungshilfen: boolean | null;
  /** „gerade“, „vor 4 min“, „gesperrt“ — oder null (unbekannt, ältere App). */
  benutzt: string | null;
  pausiert: boolean;
  /** Mac-App vor 1.1.0 (ohne Gerätekennung): Name, Wahl und Trennen gelten nur bis zum Neustart. */
  aeltere_app: boolean;
  /** 12.10.2026: versteht die App „update_starten“ (Ein-Klick-Update mit Touch ID am Mac)? Fehlt bei einem älteren Server. */
  kann_update_starten?: boolean;
}

export interface MacWahl {
  /** „hand“ = Martin hat gewählt; „automatisch“ = sonst. */
  modus: "hand" | "automatisch";
  /** Kennung des aktiven Macs — null, wenn keiner klar ist. */
  aktiv: string | null;
  /** hand, einziges, arbeit, automatik, bleibt, zuletzt-benutzt — oder unklar, keins, warten, nicht-bereit. */
  grund: string;
  hand_seit: string | null;
  /**
   * Der von Hand gewählte Mac — auch, wenn die Wahl gerade ruht (pausiert, `grund: "nicht-bereit"`) oder auf ihn
   * wartet (Neustart). `name` null, wenn der Server ihn nicht mehr kennt. Fehlt bei einem älteren Server.
   */
  hand?: { id: string; name: string | null } | null;
}

export type MacAktion = "waehlen" | "umbenennen" | "trennen" | "automatik";

/** Welche Geräteliste: Vespera Desktop (Mac) oder die Chrome-Erweiterung (07.10.2026, S6). */
export type GeraeteArt = "mac" | "browser";

/** Eine abgelehnte Aktion: HTTP-Status (null = keine Antwort) und der Fehlercode des Servers. */
export class GeraetAktionFehler extends Error {
  constructor(
    readonly status: number | null,
    readonly code: string | null,
  ) {
    super(`geraet ${status ?? "netz"} ${code ?? ""}`.trim());
    this.name = "GeraetAktionFehler";
  }
}

const AKTION_PFAD: Readonly<Record<GeraeteArt, string>> = { mac: "/api/daemon/geraet", browser: "/api/ext/geraet" };

/**
 * POST /api/daemon/geraet (Macs) bzw. POST /api/ext/geraet (Browser) — Hier arbeiten, Wieder automatisch, Umbenennen,
 * Trennen. Antwortet mit der neuen Liste (je Art in ihrer Form: MacGeraet bzw. BrowserGeraet). Wirft
 * GeraetAktionFehler (400/401/404/409/503 oder keine Verbindung). Der Server antwortet hier nie 402 (der Fetch-Wrapper
 * in app.ts öffnet bei 402 das Bezahlblatt).
 */
export async function geraetAktion(
  art: GeraeteArt,
  aktion: MacAktion,
  id?: string,
  name?: string,
): Promise<{ geraete: unknown[]; wahl: MacWahl }> {
  let res: Response;
  try {
    res = await fetch(AKTION_PFAD[art], {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ aktion, ...(id ? { id } : {}), ...(name !== undefined ? { name } : {}) }),
    });
  } catch {
    throw new GeraetAktionFehler(null, null);
  }
  const daten = (await res.json().catch(() => null)) as { error?: unknown; geraete?: unknown; wahl?: unknown } | null;
  if (!res.ok) throw new GeraetAktionFehler(res.status, typeof daten?.error === "string" ? daten.error : null);
  if (!daten || !Array.isArray(daten.geraete) || !daten.wahl || typeof daten.wahl !== "object") {
    throw new GeraetAktionFehler(res.status, "antwort-unvollstaendig");
  }
  return { geraete: daten.geraete as unknown[], wahl: daten.wahl as MacWahl };
}

interface DaemonPairingCode {
  code: string;
  expiresAt: number;
}

export async function fetchDaemonStatus(): Promise<DaemonStatus> {
  const res = await fetch("/api/daemon-status", { credentials: "same-origin" });
  if (!res.ok) throw new Error(`daemon status: ${res.status}`);
  return (await res.json()) as DaemonStatus;
}

/**
 * Einen Kopplungscode holen. `klick`: der Code geht gleich in den Link
 * vespera-desktop://verbinden — er gilt dann nur 2 statt 5 Minuten
 * (server/daemon-kopplung.ts).
 */
export async function generateDaemonPairingCode(klick = false): Promise<DaemonPairingCode> {
  const res = await fetch("/api/daemon/pair-code", {
    method: "POST",
    credentials: "same-origin",
    ...(klick
      ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify({ klick: true }) }
      : {}),
  });
  if (!res.ok) throw new Error(`daemon pair-code: ${res.status}`);
  return (await res.json()) as DaemonPairingCode;
}

// --- Standort (Pre.239) ------------------------------------------------

export interface UserLocation {
  lat: number;
  lng: number;
  accuracy?: number;
  city?: string;
  country?: string;
  updatedAt?: string;
}

export async function fetchUserLocation(): Promise<UserLocation | null> {
  const res = await fetch("/api/location", { credentials: "same-origin" });
  if (!res.ok) return null;
  const data = (await res.json()) as { location: UserLocation | null };
  return data.location;
}

export async function saveUserLocation(
  loc: UserLocation,
): Promise<UserLocation | null> {
  const res = await fetch("/api/location", {
    method: "PUT",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(loc),
  });
  if (!res.ok) throw new Error(`save location: ${res.status}`);
  const data = (await res.json()) as { location: UserLocation | null };
  return data.location;
}

export async function deleteUserLocation(): Promise<void> {
  await fetch("/api/location", {
    method: "DELETE",
    credentials: "same-origin",
  });
}

export async function fetchCoachMode(): Promise<boolean> {
  const res = await fetch("/api/coach", { credentials: "same-origin" });
  if (!res.ok) return false;
  const data = (await res.json()) as { enabled?: boolean };
  return !!data.enabled;
}

export async function setCoachMode(enabled: boolean): Promise<boolean> {
  const res = await fetch("/api/coach", {
    method: "PUT",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) throw new Error(`coach: ${res.status}`);
  const data = (await res.json()) as { enabled?: boolean };
  return !!data.enabled;
}
